const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3000";

async function request(path, { method = "GET", body, headers = {} } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    // not JSON — ignore
  }

  if (!res.ok) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.details = data?.details;
    err.action = data?.action;
    // 402 = subscription expired/trial ended. Broadcast so the app shell can
    // route the user to billing instead of each page failing in isolation.
    if (res.status === 402 && data?.action === "billing") {
      try {
        window.dispatchEvent(
          new CustomEvent("subscription-blocked", { detail: data }),
        );
      } catch {
        // non-browser env — ignore
      }
    }
    throw err;
  }
  return data;
}

export const api = {
  get: (path) => request(path),
  post: (path, body) => request(path, { method: "POST", body }),
  put: (path, body) => request(path, { method: "PUT", body }),
  patch: (path, body) => request(path, { method: "PATCH", body }),
  del: (path) => request(path, { method: "DELETE" }),
};

// ── Latest Updates (changelog) — visible to any signed-in user ──
export const getChangelog = () => request("/api/changelog");

// ── Public platform status (maintenance) — no auth required ──
export const getPublicStatus = () => request("/api/public/status");

// ── Impersonation status/stop (feature #6) ──
export const getImpersonationStatus = () => request("/api/impersonate/status");
export const stopImpersonation = () => request("/api/impersonate/stop", { method: "POST" });

// ── Coupon validation preview at checkout (feature #20) ──
export const validateCoupon = (code, plan, amount) =>
  request("/api/subscriptions/validate-coupon", { method: "POST", body: { code, plan, amount } });

export async function downloadReportsExport({ scope = "all", filter = "all", from, to, dept } = {}) {
  const qs = new URLSearchParams({ scope, filter });
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  if (dept) qs.set("dept", dept);
  const q = qs.toString();
  const res = await fetch(
    `${API_BASE}/api/reports/export.csv?${q}`,
    { credentials: "include" },
  );
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      msg = j?.error || msg;
    } catch {}
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = /filename="?([^"]+)"?/i.exec(disposition);
  const filename = match
    ? match[1]
    : `daily-reports-${new Date().toISOString().slice(0, 10)}.csv`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const createManager = (payload) =>
  api.post("/api/create-manager", payload);

export const createEmployee = (payload) =>
  api.post("/api/create-employee", payload);
export const bulkCreateEmployees = (payload) =>
  api.post("/api/create-employee/bulk", payload);

export const listEmployees = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  const q = qs.toString();
  return api.get(`/api/employees${q ? `?${q}` : ""}`);
};
export const bulkDeactivateEmployees = (userIds) =>
  api.post("/api/employees/bulk-deactivate", { userIds });

// Departments
export const listDepartments = () => api.get("/api/departments");
export const createDepartment = (body) => api.post("/api/departments", body);
export const updateDepartment = (id, body) =>
  api.patch(`/api/departments/${id}`, body);
export const deleteDepartment = (id) => api.del(`/api/departments/${id}`);

export const getAnalytics = (range = "30d", from, to) => {
  if (from && to) {
    return api.get(`/api/analytics?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
  }
  return api.get(`/api/analytics?range=${encodeURIComponent(range)}`);
};

export const getKri = () => api.get(`/api/kri`);

export const sendReminder = (userId) =>
  api.post("/api/reminders", { userId });

export const sendBulkReminder = (userIds) =>
  api.post("/api/reminders", { userIds });

export const remindAllPending = () =>
  api.post("/api/reminders", { all: true });

export const getReminderSettings = () =>
  api.get("/api/reminder-settings");

export const updateReminderSettings = (payload) =>
  api.patch("/api/reminder-settings", payload);

export async function downloadAnalyticsExport(range = "30d", from, to) {
  let qs;
  if (from && to) {
    qs = `from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
  } else {
    qs = `range=${encodeURIComponent(range)}`;
  }
  const res = await fetch(
    `${API_BASE}/api/analytics/export.csv?${qs}`,
    { credentials: "include" },
  );
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      msg = j?.error || msg;
    } catch {}
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = /filename="?([^"]+)"?/i.exec(disposition);
  const filename = match
    ? match[1]
    : `analytics-${new Date().toISOString().slice(0, 10)}.csv`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const updateEmployee = (id, payload) =>
  api.patch(`/api/employees/${id}`, payload);

export const deleteEmployee = (id) =>
  fetch(`${API_BASE}/api/employees/${id}`, {
    method: "DELETE",
    credentials: "include",
  }).then(async (r) => {
    const data = await r.json().catch(() => null);
    if (!r.ok) {
      const err = new Error(data?.error || `HTTP ${r.status}`);
      err.status = r.status;
      throw err;
    }
    return data;
  });

export async function downloadEmployeesExport({ department, status, search, from, to } = {}) {
  const qs = new URLSearchParams();
  if (department) qs.set("department", department);
  if (status) qs.set("status", status);
  if (search) qs.set("search", search);
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  const q = qs.toString();
  const res = await fetch(
    `${API_BASE}/api/employees/export.csv${q ? `?${q}` : ""}`,
    { credentials: "include" },
  );
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      msg = j?.error || msg;
    } catch {}
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = /filename="?([^"]+)"?/i.exec(disposition);
  const filename = match
    ? match[1]
    : `employees-${new Date().toISOString().slice(0, 10)}.csv`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const getInvitation = (token) =>
  api.get(`/api/invitations/${token}`);

export const acceptInvitation = (payload) =>
  api.post("/api/invitations/accept", payload);

// Step 1: capture name + password; backend returns a wa.me link the user
// taps to send a one-time WhatsApp "verify <token>" message from their phone.
export const acceptInvitationStep1 = (payload) =>
  api.post("/api/invitations/accept-step1", payload);

// Poll while waiting for the user to tap Send in WhatsApp.
export const getVerifyStatus = (token) =>
  api.get(`/api/invitations/${token}/verify-status`);

// Re-issue a fresh verifyToken + wa.me link (60s cooldown).
export const resendVerifyLink = (token) =>
  api.post("/api/invitations/resend-verify-link", { token });

// Owner pre-signup phone verification (WhatsApp click-to-chat flow).
// /send returns { verificationToken, waLink, botPhone, verifyMessage, expiresAt }.
// Frontend polls /verify-status until verified, then passes verificationToken
// into signUp.email so the backend hook can validate it.
export const sendSignupOtp = (phone) =>
  api.post("/api/signup-otp/send", { phone });

export const getSignupVerifyStatus = (token) =>
  api.get(`/api/signup-otp/${token}/verify-status`);

export const resendSignupVerifyLink = (token) =>
  api.post("/api/signup-otp/resend-verify-link", { token });

export const submitReport = (payload) =>
  api.post("/api/reports", payload);

export const listReports = ({
  from,
  to,
  search,
  filter,
  dept,
  page,
  pageSize,
  sort,
  dir,
  scope,
} = {}) => {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  if (search) qs.set("search", search);
  if (filter && filter !== "all") qs.set("filter", filter);
  if (dept) qs.set("dept", dept);
  if (page) qs.set("page", page);
  if (pageSize) qs.set("pageSize", pageSize);
  if (sort) qs.set("sort", sort);
  if (dir) qs.set("dir", dir);
  if (scope) qs.set("scope", scope);
  const q = qs.toString();
  return api.get(`/api/reports${q ? `?${q}` : ""}`);
};

export const getReportById = (id) => api.get(`/api/reports/${id}`);
export const updateReport = (id, data) => api.patch(`/api/reports/${id}`, data);
export const deleteReport = (id) => api.del(`/api/reports/${id}`);

export const emailReportsDigest = (filter = "all", ids) =>
  api.post("/api/reports/email-digest", {
    filter,
    ...(ids && ids.length ? { ids } : {}),
  });

export const getMe = () => api.get("/api/me");

export const updateMe = (payload) => api.patch("/api/me", payload);

export const updateCompany = (payload) => api.patch("/api/me/company", payload);

// Profile phone change (WhatsApp click-to-chat verification).
// /request-change returns { verificationToken, waLink, botPhone, verifyMessage, expiresAt }.
// Frontend hands verificationToken to WhatsAppVerifyStep and polls /verify-status
// until verified, then refreshes getMe() to pick up the new phone.
export const requestPhoneChange = (phone) =>
  api.post("/api/me/phone/request-change", { phone });

export const getPhoneChangeStatus = (token) =>
  api.get(`/api/me/phone/${token}/verify-status`);

export const resendPhoneChangeLink = (token) =>
  api.post("/api/me/phone/resend-verify-link", { token });

export const cancelPhoneChange = () => api.del("/api/me/phone/pending");

// ─── SMTP ─────────────────────────────────────────────────────
export const getSmtp = () => api.get("/api/smtp");
export const saveSmtp = (payload) => api.put("/api/smtp", payload);
export const testSmtp = () => api.post("/api/smtp/test");

// ─── Tasks ────────────────────────────────────────────────────
export const listTasks = ({ userId, status = "active" } = {}) => {
  const qs = new URLSearchParams();
  if (userId) qs.set("userId", userId);
  if (status) qs.set("status", status);
  return api.get(`/api/tasks?${qs.toString()}`);
};

export const listTeamTasks = ({ status = "active" } = {}) => {
  const qs = new URLSearchParams({ team: "true", status });
  return api.get(`/api/tasks?${qs.toString()}`);
};

export const createTask = (payload) => api.post("/api/tasks", payload);

export const bulkCreateTasks = (payload) => api.post("/api/tasks/bulk", payload);

export const updateTask = (id, payload) => api.patch(`/api/tasks/${id}`, payload);

export const deleteTask = (id) => api.del(`/api/tasks/${id}`);

// ─── Reports (new) ────────────────────────────────────────────
export const getReportDraft = (date) =>
  api.get(`/api/reports/draft${date ? `?date=${date}` : ""}`);

// ─── WhatsApp Bot dashboard ───────────────────────────────────
export const getWhatsappDashboard = () => api.get("/api/whatsapp/dashboard");
export const sendWhatsappTest = () => api.post("/api/whatsapp/test", {});

// ─── Subscriptions / Billing ──────────────────────────────────
export const getPlans = () => api.get("/api/subscriptions/plans");

export const createSubscription = (plan) =>
  api.post("/api/subscriptions/create", { plan });

export const verifyPayment = (payload) =>
  api.post("/api/subscriptions/verify", payload);

export const cancelSubscription = () =>
  api.post("/api/subscriptions/cancel", {});

export const upgradeSubscription = (plan) =>
  api.post("/api/subscriptions/upgrade", { plan });

export const createCustomSubscription = (payload) =>
  api.post("/api/subscriptions/create-custom", payload);

export const upgradeCustomSubscription = (payload) =>
  api.post("/api/subscriptions/upgrade-custom", payload);

export const getInvoices = () => api.get("/api/subscriptions/invoices");

// ─── Working Days ─────────────────────────────────────────────
export const getWorkingDays = () => api.get("/api/working-days");
export const updateWorkingDays = (payload) => api.patch("/api/working-days", payload);

// ─── Holidays ─────────────────────────────────────────────────
export const getHolidays = (year) => api.get(`/api/holidays?year=${year}`);
export const createHoliday = (payload) => api.post("/api/holidays", payload);
export const deleteHoliday = (id) => api.del(`/api/holidays/${id}`);
export const seedHolidays = (year) => api.post("/api/holidays/seed", year ? { year } : {});

// ─── Leaves ──────────────────────────────────────────────────
export const getLeaves = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return api.get(`/api/leaves${qs ? `?${qs}` : ""}`);
};
export const markLeave = (payload) => api.post("/api/leaves", payload);
export const cancelLeave = (id) => api.del(`/api/leaves/${id}`);
export const getMyLeaves = () => api.get("/api/leaves/mine");
export const cancelMyLeave = (id) => api.del(`/api/leaves/mine/${id}`);

// ─── Team picker (raw selectable users) ───────────────────────
// scope=company → all active company users (project members);
// default      → caller's team only (review subject). Managers+ only.
export const listTeam = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return api.get(`/api/team${qs ? `?${qs}` : ""}`);
};

// ─── Projects ─────────────────────────────────────────────────
export const getProjectsMeta = () => api.get("/api/projects/meta");
export const listProjects = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return api.get(`/api/projects${qs ? `?${qs}` : ""}`);
};
export const getProject = (id) => api.get(`/api/projects/${id}`);
export const createProject = (body) => api.post("/api/projects", body);
export const updateProject = (id, body) => api.patch(`/api/projects/${id}`, body);
export const deleteProject = (id) => api.del(`/api/projects/${id}`);
export const linkProjectTasks = (id, taskIds) =>
  api.post(`/api/projects/${id}/tasks`, { taskIds });
export const unlinkProjectTask = (id, taskId) =>
  api.del(`/api/projects/${id}/tasks/${taskId}`);
export const setProjectMembers = (id, memberIds) =>
  api.put(`/api/projects/${id}/members`, { memberIds });
export const getCandidateTasks = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return api.get(`/api/projects/candidate-tasks${qs ? `?${qs}` : ""}`);
};

// ─── Performance Reviews ──────────────────────────────────────
export const getReviewsMeta = () => api.get("/api/performance-reviews/meta");
export const listReviews = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return api.get(`/api/performance-reviews${qs ? `?${qs}` : ""}`);
};
export const getReview = (id) => api.get(`/api/performance-reviews/${id}`);
export const createReview = (body) => api.post("/api/performance-reviews", body);
export const updateReview = (id, body) => api.patch(`/api/performance-reviews/${id}`, body);
export const deleteReview = (id) => api.del(`/api/performance-reviews/${id}`);
export const acknowledgeReview = (id) =>
  api.post(`/api/performance-reviews/${id}/acknowledge`, {});
export const getReviewsUnreadCount = () =>
  api.get("/api/performance-reviews/unread-count");
export const getReviewPeriodSummary = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return api.get(`/api/performance-reviews/period-summary${qs ? `?${qs}` : ""}`);
};

// ─── Announcements ─────────────────────────────────────────────────
export const listAnnouncements = (q = {}) => {
  const qs = new URLSearchParams();
  if (q.page) qs.set("page", q.page);
  if (q.limit) qs.set("limit", q.limit);
  if (q.search) qs.set("search", q.search);
  if (q.pinned !== undefined) qs.set("pinned", q.pinned);
  if (q.sort) qs.set("sort", q.sort);
  if (q.dir) qs.set("dir", q.dir);
  return api.get(`/api/announcements?${qs.toString()}`);
};

export const getAnnouncement = (id) => api.get(`/api/announcements/${id}`);
export const createAnnouncement = (body) => api.post("/api/announcements", body);
export const updateAnnouncement = (id, body) => api.patch(`/api/announcements/${id}`, body);
export const deleteAnnouncement = (id) => api.del(`/api/announcements/${id}`);
export const pinAnnouncement = (id) => api.post(`/api/announcements/${id}/pin`);
export const unpinAnnouncement = (id) => api.post(`/api/announcements/${id}/unpin`);

// Lightweight endpoint for reminder/dashboard use
export const getActivePinnedAnnouncements = () => api.get("/api/announcements/active-pinned");

// ─── Activity log (tenant audit) ──────────────────────────────
export const listAuditLog = (qs = "") => api.get(`/api/audit${qs}`);
export const listAuditActors = () => api.get("/api/audit/actors");

// ─── Notifications ────────────────────────────────────────────
export const listNotifications = (limit = 30) =>
  api.get(`/api/notifications?limit=${limit}`);
export const getNotificationsUnreadCount = () =>
  api.get("/api/notifications/unread-count");
export const markNotificationRead = (id) =>
  api.post(`/api/notifications/${id}/read`, {});
export const markAllNotificationsRead = () =>
  api.post("/api/notifications/read-all", {});